import React, { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import {
  Avatar,
  Box,
  Button,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  UserRound,
  Store,
  Package,
  Lock,
  Inbox,
  ClipboardList,
  Star,
  Settings,
  LogOut,
} from 'lucide-react';

import PropTypes from 'prop-types';

import { useAuthContext } from '../../../Context/AuthContext';

import { COLORS } from '../../../Constant/COLORS';
import FONTS from '../../../Constant/FONTS';

import Header from '../../../Components/Header';
import Footer from '../../../Components/Footer';
import { TypoPoppins } from '../../../Components/Typo';
import { PageFade } from '../../../Components/Motion';
import FooterGetToKnow from '../../../Components/FooterGetToKnow';
import MobileNavigationLayout from './MobileNavigationLayout';
import NavLinkLayout from './NavLinkLayout';

import { useIsMobile } from '../../../Hooks/useIsMobile';
import useHandleLogout from '../../../Hooks/useHandleLogout';
import { LoadingButton } from '../../../Components/Loaders';

const Layout = ({
  isAvatarButtonsAvailable = 'true',
  children,
  message,
  handleClickCancel,
  handleClickSave,
  isSaveDisabled,
}) => {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const handleLogout = useHandleLogout();

  const [profDetails, setProfDetails] = useState({ fullName: '', avatarURL: '' });

  const isMobile = useIsMobile();

  const onLogoutClick = async () => {
    await handleLogout();
    navigate('/professional/login');
  };

  // const fetchProfessionalDetails = useCallback(async () => {
  //   const query = FindProfessional({ professionalUniqueID: user.userID });
  //   const { data, errors } = await Axios({ query });

  //   if (errors) return undefined;

  //   const { first_name, last_name, avatar } = data.Professional;
  //   setProfDetails({ fullName: first_name.concat(' ', last_name), avatarURL: avatar });
  // }, [user.userID]);

  // useEffect(() => {
  //   fetchProfessionalDetails();
  // }, [fetchProfessionalDetails]);

  return (
    <>
      <Header isProfessionalPage />

      {isMobile && <MobileNavigationLayout />}

      <Box px={{ xs: 2, md: '10%' }} mx='auto' mt={{ xs: 2.5, md: 5 }}>
        {!isMobile && (
          <>
            <Typography variant='h4' my={1.5}>
              Profile
            </Typography>
            <Divider sx={{ borderColor: COLORS.hairline }} />
          </>
        )}

        <Stack mt={{ md: 3 }} direction='row' ml={{ md: -2 }} gap={8}>
          {!isMobile && (
            <List
              disablePadding
              sx={{
                whiteSpace: 'nowrap',
                p: 1.5,
                border: `1px solid ${COLORS.hairline}`,
                borderRadius: 3,
                bgcolor: COLORS.surface,
                height: 'fit-content',
                minWidth: 210,
              }}>
              <NavLinkLayout to='my-details' icon={UserRound}>My Details</NavLinkLayout>
              <NavLinkLayout to='service' icon={Store}>Service</NavLinkLayout>
              <NavLinkLayout to='packages' icon={Package}>Packages</NavLinkLayout>
              <NavLinkLayout to='password' icon={Lock}>Password</NavLinkLayout>
              <NavLinkLayout to='job-requests/response' icon={Inbox}>Job Requests</NavLinkLayout>
              <NavLinkLayout to='job-information/in-complete' icon={ClipboardList}>Job Information</NavLinkLayout>
              <NavLinkLayout to='review-manage' icon={Star}>Review Manage</NavLinkLayout>
              {/* <NavLinkLayout to='accountDetails'>Account Details</NavLinkLayout> */}
              <Tooltip title='Feature not available' placement='bottom-end'>
                <ListItemButton
                  disabled
                  sx={{ borderRadius: 2, gap: 1.25, fontWeight: 600, color: COLORS.inkSoft, opacity: 0.5 }}
                >
                  <Settings size={18} strokeWidth={1.8} />
                  Account Details
                </ListItemButton>
              </Tooltip>

              <Divider sx={{ my: 1.5, borderColor: COLORS.hairline }} />

              <ListItem disableGutters disablePadding>
                <ListItemButton
                  onClick={onLogoutClick}
                  sx={{
                    color: COLORS.red3,
                    borderRadius: 2,
                    gap: 1.25,
                    fontWeight: 600,
                    '&:hover': { backgroundColor: 'rgba(255,33,33,0.06)' },
                  }}
                >
                  <LogOut size={18} strokeWidth={1.8} />
                  Logout
                </ListItemButton>
              </ListItem>
            </List>
          )}

          <Box flexGrow={1}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems='center'
              gap={{ xs: 2, md: 4 }}
              mb={{ xs: 2, md: 8 }}
              justifyContent={{ xs: 'center', md: 'unset' }}
            >
              <Avatar
                sx={{ width: { xs: 120, sm: 90 }, height: { xs: 120, sm: 90 } }}
                src={profDetails.avatarURL}
              />

              <Box>
                <Typography
                  variant={isMobile ? 'h5' : 'h4'}
                  color={COLORS.teal}
                  textAlign={{ xs: 'center', md: 'unset' }}
                >
                  {profDetails.fullName}
                </Typography>
                <TypoPoppins
                  color={COLORS.inkSoft}
                  textAlign={{ xs: 'center', md: 'unset' }}
                >
                  {message}
                </TypoPoppins>
              </Box>

              {isAvatarButtonsAvailable && (
                <Grid container columns={2} columnSpacing={4} mb={2.5} maxWidth='240px'>
                  <Grid item xs={1}>
                    <Button
                      fullWidth
                      variant='outlined'
                      color='blue'
                      sx={{ color: COLORS.blue }}
                      onClick={handleClickCancel}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={1}>
                    <LoadingButton
                      color='blue'
                      fullWidth
                      isLoading={isSaveDisabled}
                      onClick={handleClickSave}
                    >
                      Save
                    </LoadingButton>
                  </Grid>
                </Grid>
              )}
            </Stack>
            <Box
              pl={{ md: 10 }}
              sx={{
                '& .MuiFormLabel-root,& .MuiOutlinedInput-root': {
                  fontFamily: FONTS.POPP,
                  color: COLORS.black,
                  fontSize: '.875rem',
                },
              }}
            >
              <PageFade>{children}</PageFade>
            </Box>
          </Box>
        </Stack>
      </Box>

      <Box mt='10vh'>
        <FooterGetToKnow />
        <Footer isRegisterProfessionalPage />
      </Box>
    </>
  );
};

Layout.propTypes = {
  isAvatarButtonsAvailable: PropTypes.bool,
  children: PropTypes.node,
  message: PropTypes.string,
  handleClickCancel: PropTypes.func,
  handleClickSave: PropTypes.func,
};

export default Layout;
